import supersedingCondition from "@shared/clinical-logic/helpers/supersedingHelper";
import severities from "@shared/clinical-logic/all/severity";

const TRIAGE_ORDER = ['none', 'inconclusive', 'standard', 'immediate', 'urgent'];
const DEFAULT_SUPPRESS_ALL_MIN_SEVERITY = 'risk_present';

type SupersedingRuleCondition = {
    assessment?: string;
    min_severity?: string;
};

type TrackScopeValue = string | number;

type CurrentTrack = {
    id?: TrackScopeValue;
    name?: TrackScopeValue;
    path?: TrackScopeValue;
    [key: string]: unknown;
};

type AssessmentSupersedingRule = {
    id?: string;
    action?: string;
    tracks?: TrackScopeValue | TrackScopeValue[];
    triggers?: SupersedingRuleCondition[];
    suppress?: SupersedingRuleCondition[] | 'all';
    display?: SupersedingRuleCondition[];
    suppress_min_severity?: string;
};

type NormalizedSupersedingRule = {
    id?: string;
    condition: string;
    condition_min_severity?: string;
    superseding_condition: string;
    superseding_min_severity?: string;
    action: string;
};

type EngagementFeatures = {
    assessmentSupersedingRules?: AssessmentSupersedingRule[];
};

type ResultItem = {
    type?: string;
    name?: string;
    assessment?: string;
    display_hidden?: boolean;
    display_hidden_reason?: string;
    triage_recommendation?: string;
    selected?: boolean;
    shown?: boolean;
    [key: string]: unknown;
};

export const getTrackPath = (track: CurrentTrack = {}): string | null => {
    return track.path === undefined || track.path === null ? null : String(track.path)
}

export const ruleMatchesTrack = (rule: AssessmentSupersedingRule,
                                 currentTrack: CurrentTrack = {}): boolean => {
    if (!rule?.tracks) {
        return true;
    }

    const configuredTracks = Array.isArray(rule.tracks) ? rule.tracks : [rule.tracks];
    if (configuredTracks.length === 0) {
        return true;
    }

    const currentTrackPath = getTrackPath(currentTrack)
    if (!currentTrackPath) {
        return false;
    }

    return configuredTracks
        .map(track => String(track))
        .some(track => track === currentTrackPath);
}

const severityAbove = (actualSeverity?: string, minSeverity?: string): boolean => {
    if (!minSeverity) {
        return true;
    }

    const actual = severities[actualSeverity];
    const minimum = severities[minSeverity];

    if (!actual || !minimum) {
        return false;
    }

    return actual.value >= minimum.value;
}

const conditionMatches = (condition: SupersedingRuleCondition,
                          assessment: ResultItem): boolean => {
    if (!condition?.assessment || !assessment?.name || condition.assessment !== assessment.name) {
        return false;
    }

    return severityAbove(assessment.severity as string | undefined, condition.min_severity);
}

const getConditionList = (conditions?: SupersedingRuleCondition[]): SupersedingRuleCondition[] => {
    return Array.isArray(conditions) ? conditions.filter(condition => condition?.assessment) : [];
}

const displayAssessment = (assessment: ResultItem,
                           displayConditions: SupersedingRuleCondition[] = []): boolean => {
    if (!assessment?.name || displayConditions.length === 0) {
        return false;
    }

    return displayConditions.some(condition => conditionMatches(condition, assessment));
}

const displayCondition = (condition: SupersedingRuleCondition,
                          displayConditions: SupersedingRuleCondition[] = [],
                          assessmentsByName = new Map<string, ResultItem>()): boolean => {

    if (!condition?.assessment) {
        return false;
    }

    const assessment = assessmentsByName.get(condition.assessment);
    if (assessment) {
        return displayAssessment(assessment, displayConditions);
    }

    return displayConditions.some(
      displayCondition => displayCondition.assessment === condition.assessment
    );
}

const normalizeSuppressConditions = (rule: AssessmentSupersedingRule,
                                     assessments: ResultItem[] = []): SupersedingRuleCondition[] => {

    const displayConditions = getConditionList(rule.display);
    const triggerNames = new Set(getConditionList(rule.triggers)
      .map(trigger => trigger.assessment));
    const suppressAllMinSeverity = rule.suppress_min_severity
      || DEFAULT_SUPPRESS_ALL_MIN_SEVERITY;
    const assessmentsByName = new Map(
        assessments
            .filter(assessment => assessment?.name)
            .map(assessment => [assessment.name, assessment]),
    );

    if (rule.suppress === 'all') {
        return assessments
            .filter(assessment => assessment?.name)
            .filter(assessment => !triggerNames.has(assessment.name))
            .filter(assessment => !displayAssessment(assessment, displayConditions))
            .filter(assessment =>
              severityAbove(assessment.severity as string | undefined, suppressAllMinSeverity))
            .map(assessment => ({
                assessment: assessment.name,
                min_severity: suppressAllMinSeverity,
            }));
    }

    return getConditionList(rule.suppress)
        .filter(condition => !displayCondition(condition, displayConditions, assessmentsByName));
}

export const normalizeSupersedingRules = (rules: AssessmentSupersedingRule[] = [],
                                          currentTrack: CurrentTrack = {},
                                          assessments: ResultItem[] = []): NormalizedSupersedingRule[] => {

    if (!Array.isArray(rules)) {
        return [];
    }
    return rules.flatMap(rule => {
        if(!rule || !Array.isArray(rule.triggers)) {
            return [];
        }

        if (!ruleMatchesTrack(rule, currentTrack)) {
            return [];
        }

        const suppressConditions = normalizeSuppressConditions(rule, assessments);

        if (suppressConditions.length === 0) {
            return [];
        }

        return rule.triggers.flatMap(trigger => {
            if (!trigger?.assessment) {
                return [];
            }

            return suppressConditions.map(suppress => ({
                id: rule.id,
                condition: suppress.assessment,
                condition_min_severity: suppress.min_severity,
                superseding_condition: trigger.assessment,
                superseding_min_severity: trigger.min_severity,
                action: rule.action || 'display_hidden'
            }));
        })
    })
}


export const applyCustomSupersedingRules = (
    results: ResultItem[],
    engagementFeatures: EngagementFeatures = {},
    currentTrack: CurrentTrack = {},
): ResultItem[] => {
    const assessments = results.filter(item => item.type === 'assessment');
    const configuredRules = normalizeSupersedingRules(
        engagementFeatures?.assessmentSupersedingRules,
        currentTrack,
        assessments,
    );

    if (configuredRules.length === 0) {
        return results;
    }

    supersedingCondition(assessments, configuredRules);

    return results;
}

export const getVisibleTriage = (assessments = []) => {
    let triage = 'none';
    assessments
        .filter(assessment => !assessment.display_hidden)
        .forEach(assessment => {
            const recommendation = assessment.triage_recommendation || 'none'
            const currentOrder = TRIAGE_ORDER.findIndex(value => value === triage)
            const nextOrder = TRIAGE_ORDER.findIndex(value => value === recommendation)
            if (nextOrder > currentOrder) {
                triage = recommendation
            }
        });
    return triage;
}

export const applyDisplayHiddenMetadata = (results) => {
    const hiddenAssessments = new Map(
        results
            .filter(
                item => item.type === 'assessment' && item.display_hidden && !!item.name,
            )
            .map(item => [item.name, item.display_hidden_reason]),
    );

    if (hiddenAssessments.size > 0) {
        results.forEach(item => {
            if (!['plan', 'treatment'].includes(item.type) || !item.assessment) {
                return;
            }

            const reason = hiddenAssessments.get(item.assessment)
            if (!reason) {
                return;
            }

            item.display_hidden = true;
            item.display_hidden_reason = reason;
            item.selected = false;

            if (item.type === 'plan') {
                item.shown = false;
            }
        });
    }

    const triage = results.find(item => item.type === 'triage');
    if (triage) {
        const assessments = results.filter(item => item.type === 'assessment');
        triage.name = getVisibleTriage(assessments);
    }

    return results;
}
